/**
 * Schedule picker ↔ cron. The planner never invents schedule semantics: it builds
 * a 5-field expression here and the SERVER answers what it means and when it fires
 * (preview-schedule), because the same parser has to run the task later.
 *
 * These helpers are pure so the round-trip (plan → cron → plan) is testable without
 * a browser: an edited task must re-open on the preset it was created with.
 */

export type SchedulePresetKind =
  | "hourly"
  | "daily"
  | "weekdays"
  | "weekly"
  | "monthly"
  | "custom";

export interface SchedulePlan {
  kind: SchedulePresetKind;
  /** Hour of day (0–23) for the day/week/month presets. */
  hour: number;
  /** Minute past the hour (0–59) — also the offset minute of the "every N hours" preset. */
  minute: number;
  /** Cron day-of-week tokens for the weekly preset ("mon" … "sun"). */
  weekdays: string[];
  /** Day of month (1–28; 29–31 would skip short months). */
  dayOfMonth: number;
  /** N of the "every N hours" preset. */
  everyHours: number;
  /** Raw expression — the source of truth for kind === "custom". */
  cron: string;
}

export const WEEKDAY_TOKENS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

export const WEEKDAY_LABELS: Record<string, string> = {
  mon: "pon",
  tue: "wt",
  wed: "śr",
  thu: "czw",
  fri: "pt",
  sat: "sob",
  sun: "nd",
};

const WORKDAYS = ["mon", "tue", "wed", "thu", "fri"];

export const DEFAULT_PLAN: SchedulePlan = {
  kind: "daily",
  hour: 8,
  minute: 0,
  weekdays: ["mon"],
  dayOfMonth: 1,
  everyHours: 3,
  cron: "0 8 * * *",
};

const pad = (n: number) => String(n).padStart(2, "0");

const clamp = (n: number, lo: number, hi: number) =>
  Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.trunc(n))) : lo;

export function planToCron(plan: SchedulePlan): string {
  const m = clamp(plan.minute, 0, 59);
  const h = clamp(plan.hour, 0, 23);
  switch (plan.kind) {
    case "hourly":
      return `${m} */${clamp(plan.everyHours, 1, 23)} * * *`;
    case "daily":
      return `${m} ${h} * * *`;
    case "weekdays":
      return `${m} ${h} * * mon-fri`;
    case "weekly": {
      const days = plan.weekdays.length ? plan.weekdays : ["mon"];
      // Keep calendar order regardless of the click order in the picker.
      const ordered = WEEKDAY_TOKENS.filter((d) => days.includes(d));
      return `${m} ${h} * * ${ordered.join(",")}`;
    }
    case "monthly":
      return `${m} ${h} ${clamp(plan.dayOfMonth, 1, 28)} * *`;
    default:
      return plan.cron.trim();
  }
}

/**
 * Best-effort inverse: recognise the expressions the presets emit (and the common
 * shapes ARON writes in chat) so an existing task re-opens on its own preset.
 * Anything else lands on "custom" with the raw expression — never a wrong guess.
 */
export function cronToPlan(expr: string): SchedulePlan {
  const raw = (expr || "").trim();
  const base: SchedulePlan = { ...DEFAULT_PLAN, cron: raw, kind: "custom" };
  const fields = raw.split(/\s+/);
  if (fields.length !== 5) return base;
  const [minute, hour, dom, month, dow] = fields;
  if (month !== "*") return base;

  const minuteNum = /^\d+$/.test(minute) ? Number(minute) : null;
  if (minuteNum === null) return base;
  const plan = { ...base, minute: minuteNum };

  // "co N godzin"
  const everyHours = /^\*\/(\d+)$/.exec(hour);
  if (everyHours && dom === "*" && dow === "*") {
    return { ...plan, kind: "hourly", everyHours: clamp(Number(everyHours[1]), 1, 23) };
  }

  if (!/^\d+$/.test(hour)) return base;
  const withTime = { ...plan, hour: Number(hour) };

  if (dom === "*" && dow === "*") return { ...withTime, kind: "daily" };

  if (dom === "*" && dow !== "*") {
    const days = normalizeWeekdays(dow);
    if (!days) return base;
    if (days.length === WORKDAYS.length && WORKDAYS.every((d) => days.includes(d))) {
      return { ...withTime, kind: "weekdays", weekdays: days };
    }
    return { ...withTime, kind: "weekly", weekdays: days };
  }

  if (/^\d+$/.test(dom) && dow === "*") {
    return { ...withTime, kind: "monthly", dayOfMonth: clamp(Number(dom), 1, 28) };
  }
  return base;
}

/** Cron day-of-week field → canonical tokens, or null when it is not a plain list/range. */
function normalizeWeekdays(dow: string): string[] | null {
  const numeric: Record<string, string> = {
    "0": "sun", "1": "mon", "2": "tue", "3": "wed",
    "4": "thu", "5": "fri", "6": "sat", "7": "sun",
  };
  const token = (part: string): string | null => {
    const p = part.trim().toLowerCase();
    if (numeric[p]) return numeric[p];
    return (WEEKDAY_TOKENS as readonly string[]).includes(p) ? p : null;
  };
  const out = new Set<string>();
  for (const part of dow.split(",")) {
    const range = /^([a-z0-9]+)-([a-z0-9]+)$/i.exec(part.trim());
    if (range) {
      const from = token(range[1]);
      const to = token(range[2]);
      if (!from || !to) return null;
      const start = WEEKDAY_TOKENS.indexOf(from as (typeof WEEKDAY_TOKENS)[number]);
      const end = WEEKDAY_TOKENS.indexOf(to as (typeof WEEKDAY_TOKENS)[number]);
      if (start < 0 || end < 0 || end < start) return null;
      for (let i = start; i <= end; i += 1) out.add(WEEKDAY_TOKENS[i]);
      continue;
    }
    const single = token(part);
    if (!single) return null;
    out.add(single);
  }
  if (!out.size) return null;
  return WEEKDAY_TOKENS.filter((d) => out.has(d));
}

/** Short label for the preset buttons and the summary line above the preview. */
export function planLabel(plan: SchedulePlan): string {
  const at = `o ${pad(clamp(plan.hour, 0, 23))}:${pad(clamp(plan.minute, 0, 59))}`;
  switch (plan.kind) {
    case "hourly":
      return `co ${clamp(plan.everyHours, 1, 23)} godz. (minuta ${clamp(plan.minute, 0, 59)})`;
    case "daily":
      return `codziennie ${at}`;
    case "weekdays":
      return `w dni robocze ${at}`;
    case "weekly": {
      const days = WEEKDAY_TOKENS.filter((d) => plan.weekdays.includes(d));
      const names = (days.length ? days : ["mon"]).map((d) => WEEKDAY_LABELS[d]);
      return `${names.join(", ")} ${at}`;
    }
    case "monthly":
      return `${clamp(plan.dayOfMonth, 1, 28)}. dnia miesiąca ${at}`;
    default:
      return plan.cron.trim() || "—";
  }
}
